import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarRange, ClipboardList, Mail, Newspaper, Images, Settings as SettingsIcon, LogOut, Plus, Pencil, Copy, Trash2, Eye, EyeOff, ExternalLink, Handshake, Users, HandHeart, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import EventForm from "./EventForm";

const TABS = [
  ["events", "Events", CalendarRange],
  ["registrations", "Registrations", ClipboardList],
  ["donations", "Donations", HandHeart],
  ["volunteers", "Volunteers", HandHeart],
  ["enquiries", "Enquiries", Mail],
  ["stories", "Stories", Newspaper],
  ["gallery", "Gallery", Images],
  ["partners", "Partners", Handshake],
  ["team", "Team", Users],
  ["settings", "Settings", SettingsIcon],
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("events");
  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [stories, setStories] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [partners, setPartners] = useState([]);
  const [team, setTeam] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, object=edit
  const [regEventFilter, setRegEventFilter] = useState("all");
  const [donations, setDonations] = useState([]);

  const regEvents = [...new Set(registrations.map((r) => r.event_name).filter(Boolean))].sort();
  const filteredRegs = regEventFilter === "all" ? registrations : registrations.filter((r) => r.event_name === regEventFilter);

  const exportRegistrationsCSV = () => {
    const header = ["Reg ID", "Name", "Event", "Distance", "Payment", "Fee (INR)", "Email", "Phone", "City", "T-Shirt", "Date"];
    const rows = filteredRegs.map((r) => [r.reg_id, r.name, r.event_name, r.distance, r.status || "received", r.fee_amount || "", r.email, r.phone, r.city || "", r.tshirt_size || "", (r.created_at || "").slice(0, 10)]);
    const csv = [header, ...rows].map((row) => row.map((c) => `"${String(c ?? "").replaceAll('"', '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `sportive-registrations${regEventFilter === "all" ? "" : "-" + regEventFilter.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportDonationsCSV = () => {
    const header = ["Donation ID", "Name", "Email", "Cause", "Amount (INR)", "Status", "Payment ID", "Date"];
    const rows = donations.map((d) => [d.don_id, d.name, d.email, d.cause || "General", d.amount || "", d.status || "", d.payment_id || "", (d.created_at || "").slice(0, 10)]);
    const csv = [header, ...rows].map((row) => row.map((c) => `"${String(c ?? "").replaceAll('"', '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "sportive-donations.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const load = () => {
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/admin/registrations").then((r) => setRegistrations(r.data)).catch(() => {});
    api.get("/admin/volunteers").then((r) => setVolunteers(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/admin/stories").then((r) => setStories(r.data)).catch(() => {});
    api.get("/admin/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => {});
    api.get("/admin/team").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/admin/donations").then((r) => setDonations(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const onLogout = async () => { await logout(); navigate("/admin/login"); };

  const togglePublish = async (e) => {
    try {
      await api.put(`/admin/events/${e.id}`, { ...e, published: !e.published });
      toast.success(e.published ? "Event unpublished" : "Event published");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const duplicate = async (e) => {
    try { await api.post(`/admin/events/${e.id}/duplicate`); toast.success("Event duplicated (unpublished)"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removeEvent = async (e) => {
    if (!window.confirm(`Delete "${e.name}" permanently?`)) return;
    try { await api.delete(`/admin/events/${e.id}`); toast.success("Event deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...settings, phones: typeof settings.phones === "string" ? settings.phones.split(",").map((x) => x.trim()).filter(Boolean) : settings.phones };
      await api.put("/admin/settings", payload);
      setSettings(payload);
      toast.success("Settings saved");
    }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [storyForm, setStoryForm] = useState(null);
  const saveStory = async (e) => {
    e.preventDefault();
    try {
      if (storyForm.id) await api.put(`/admin/stories/${storyForm.id}`, storyForm);
      else await api.post("/admin/stories", storyForm);
      toast.success("Story saved"); setStoryForm(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [galleryForm, setGalleryForm] = useState({ url: "", category: "EVENTS", caption: "" });
  const [partnerForm, setPartnerForm] = useState({ name: "", logo: "", url: "" });
  const addPartner = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/partners", partnerForm); toast.success("Partner added"); setPartnerForm({ name: "", logo: "", url: "" }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const [memberForm, setMemberForm] = useState(null);
  const saveMember = async (e) => {
    e.preventDefault();
    try {
      if (memberForm.id) await api.put(`/admin/team/${memberForm.id}`, memberForm);
      else await api.post("/admin/team", memberForm);
      toast.success("Team member saved"); setMemberForm(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const addGallery = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/gallery", galleryForm); toast.success("Photo added"); setGalleryForm({ url: "", category: "EVENTS", caption: "" }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-brand-ink">
      <header className="glass sticky top-0 z-40 border-b border-brand-line">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/sportive-logo.png" alt="SPORTIVE" className="h-8 drop-shadow-[0_0_12px_rgba(155,174,27,0.4)]" />
            <span className="font-mono text-[10px] tracking-[0.25em] text-brand-cyan">ADMIN CMS</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-xs text-slate-500 sm:block">{user?.email}</span>
            <Link to="/" data-testid="admin-view-site" className="text-xs text-brand-olive hover:underline">View site</Link>
            <button data-testid="admin-logout" onClick={onLogout} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-400 transition-colors"><LogOut size={14} /> Logout</button>
          </div>
        </div>
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 pb-2 sm:px-6">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 font-mono text-[11px] tracking-[0.12em] transition-colors ${tab === k ? "bg-gradient-to-r from-brand-cyan to-brand-olive font-bold text-brand-ink" : "text-slate-400 hover:text-white"}`}>
              <Icon size={13} /> {label.toUpperCase()}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        {tab === "events" && (
          <div>
            <div className="flex items-center justify-between">
              <h1 className="font-display text-3xl font-bold uppercase text-white">Events ({events.length})</h1>
              <button data-testid="admin-add-event" onClick={() => setEditing(null)} className="btn-kinetic btn-primary-k !px-5 !py-2.5 text-xs"><Plus size={15} /> ADD EVENT</button>
            </div>
            <div className="card-k mt-6 overflow-x-auto">
              <table className="w-full min-w-[800px] text-left text-sm">
                <thead><tr className="border-b border-brand-line font-mono text-[10px] tracking-[0.2em] text-slate-500">
                  <th className="px-5 py-3">EVENT</th><th className="px-5 py-3">DATE</th><th className="px-5 py-3">STATE</th><th className="px-5 py-3">STATUS</th><th className="px-5 py-3">VISIBILITY</th><th className="px-5 py-3">ACTIONS</th>
                </tr></thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} data-testid={`admin-event-row-${e.slug}`} className="border-b border-brand-line/50 last:border-0 hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5">
                        <p className="font-medium text-white">{e.name}</p>
                        <p className="font-mono text-[10px] text-slate-500">{e.category}{e.featured ? " • FEATURED" : ""}</p>
                      </td>
                      <td className="px-5 py-3.5 text-slate-400">{e.date_text}</td>
                      <td className="px-5 py-3.5 text-slate-400">{e.state}</td>
                      <td className="px-5 py-3.5"><StatusBadge status={e.status} /></td>
                      <td className="px-5 py-3.5">
                        <button data-testid={`publish-toggle-${e.slug}`} onClick={() => togglePublish(e)} className={`flex items-center gap-1.5 font-mono text-[10px] tracking-wider ${e.published ? "text-brand-olive" : "text-slate-500"}`}>
                          {e.published ? <Eye size={13} /> : <EyeOff size={13} />} {e.published ? "PUBLISHED" : "HIDDEN"}
                        </button>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex gap-2">
                          <Link data-testid={`view-${e.slug}`} to={`/events/${e.slug}`} className="rounded-lg border border-brand-line p-2 text-slate-400 hover:text-brand-cyan"><ExternalLink size={14} /></Link>
                          <button data-testid={`edit-${e.slug}`} onClick={() => setEditing(e)} className="rounded-lg border border-brand-line p-2 text-slate-400 hover:text-brand-cyan"><Pencil size={14} /></button>
                          <button data-testid={`duplicate-${e.slug}`} onClick={() => duplicate(e)} className="rounded-lg border border-brand-line p-2 text-slate-400 hover:text-brand-olive"><Copy size={14} /></button>
                          <button data-testid={`delete-${e.slug}`} onClick={() => removeEvent(e)} className="rounded-lg border border-brand-line p-2 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "donations" && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="font-display text-3xl font-bold uppercase text-white">Donations ({donations.length})</h1>
              <div className="flex items-center gap-4">
                <p data-testid="donations-total" className="font-mono text-xs tracking-wider text-brand-olive">COLLECTED: ₹{donations.filter((d) => d.status === "paid").reduce((sum, d) => sum + (d.amount || 0), 0).toLocaleString("en-IN")}</p>
                <button data-testid="donations-export-csv" onClick={exportDonationsCSV} disabled={donations.length === 0} className="btn-kinetic btn-primary-k !px-5 !py-2.5 text-xs disabled:opacity-60"><Download size={15} /> EXPORT CSV</button>
              </div>
            </div>
            <div className="card-k mt-6 overflow-x-auto">
              <table className="w-full min-w-[800px] text-left text-sm">
                <thead><tr className="border-b border-brand-line font-mono text-[10px] tracking-[0.2em] text-slate-500">
                  <th className="px-5 py-3">DONATION ID</th><th className="px-5 py-3">DONOR</th><th className="px-5 py-3">CAUSE</th><th className="px-5 py-3">AMOUNT</th><th className="px-5 py-3">STATUS</th><th className="px-5 py-3">PAYMENT ID</th><th className="px-5 py-3">DATE</th>
                </tr></thead>
                <tbody>
                  {donations.map((d) => (
                    <tr key={d.id} data-testid={`admin-donation-row-${d.don_id}`} className="border-b border-brand-line/50 last:border-0 hover:bg-white/[0.02]">
                      <td className="px-5 py-3.5 font-mono text-xs text-brand-cyan">{d.don_id}</td>
                      <td className="px-5 py-3.5"><p className="font-medium text-white">{d.name}</p><p className="text-xs text-slate-500">{d.email}</p></td>
                      <td className="px-5 py-3.5 text-xs text-slate-400">{d.cause || "General"}</td>
                      <td className="px-5 py-3.5 font-semibold text-brand-olive">₹{(d.amount || 0).toLocaleString("en-IN")}</td>
                      <td className="px-5 py-3.5"><span className={`rounded-full px-2.5 py-1 font-mono text-[10px] tracking-wider ${d.status === "paid" ? "bg-brand-olive/15 text-brand-olive" : "bg-amber-500/10 text-amber-400"}`}>{(d.status || "").toUpperCase().replace("_", " ")}</span></td>
                      <td className="px-5 py-3.5 font-mono text-[11px] text-slate-500">{d.payment_id || "—"}</td>
                      <td className="px-5 py-3.5 text-xs text-slate-400">{(d.created_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                  {donations.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No donations yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "registrations" && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="font-display text-3xl font-bold uppercase text-white">
                Registrations ({filteredRegs.length}{regEventFilter !== "all" ? ` of ${registrations.length}` : ""})
              </h1>
              <div className="flex flex-wrap items-center gap-3">
                <select data-testid="registrations-event-filter" value={regEventFilter} onChange={(e) => setRegEventFilter(e.target.value)} className="input-k !w-auto !py-2.5 text-xs">
                  <option value="all">ALL EVENTS</option>
                  {regEvents.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <button data-testid="registrations-export-csv" onClick={exportRegistrationsCSV} disabled={filteredRegs.length === 0} className="btn-kinetic btn-primary-k !px-5 !py-2.5 text-xs disabled:opacity-60"><Download size={15} /> EXPORT CSV</button>
              </div>
            </div>
            <div className="card-k mt-6 overflow-x-auto">
              <table className="w-full min-w-[800px] text-left text-sm">
                <thead><tr className="border-b border-brand-line font-mono text-[10px] tracking-[0.2em] text-slate-500">
                  <th className="px-5 py-3">REG ID</th><th className="px-5 py-3">NAME</th><th className="px-5 py-3">EVENT</th><th className="px-5 py-3">DISTANCE</th><th className="px-5 py-3">PAYMENT</th><th className="px-5 py-3">CONTACT</th><th className="px-5 py-3">DATE</th>
                </tr></thead>
                <tbody>
                  {filteredRegs.map((r) => (
                    <tr key={r.id} data-testid={`reg-row-${r.reg_id}`} className="border-b border-brand-line/50 last:border-0">
                      <td className="px-5 py-3.5 font-mono text-brand-olive">{r.reg_id}</td>
                      <td className="px-5 py-3.5 font-medium text-white">{r.name}</td>
                      <td className="px-5 py-3.5 text-slate-400">{r.event_name}</td>
                      <td className="px-5 py-3.5 font-mono text-brand-cyan">{r.distance}</td>
                      <td className="px-5 py-3.5">
                        <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] tracking-widest ${r.status === "paid" ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-400" : r.status === "pending_payment" ? "border-yellow-400/40 bg-yellow-400/10 text-yellow-400" : "border-slate-400/40 bg-slate-400/10 text-slate-400"}`}>
                          {(r.status === "paid" ? `PAID${r.fee_amount ? " ₹" + r.fee_amount : ""}` : r.status || "RECEIVED").toUpperCase()}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-400">{r.email}<br />{r.phone}</td>
                      <td className="px-5 py-3.5 text-xs text-slate-500">{r.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                  {filteredRegs.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No registrations{regEventFilter !== "all" ? " for this event" : ""} yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "volunteers" && (
          <div>
            <h1 className="font-display text-3xl font-bold uppercase text-white">Volunteers ({volunteers.length})</h1>
            <div className="card-k mt-6 overflow-x-auto">
              <table className="w-full min-w-[700px] text-left text-sm">
                <thead><tr className="border-b border-brand-line font-mono text-[10px] tracking-[0.2em] text-slate-500">
                  <th className="px-5 py-3">NAME</th><th className="px-5 py-3">EVENT</th><th className="px-5 py-3">CONTACT</th><th className="px-5 py-3">CITY</th><th className="px-5 py-3">DATE</th>
                </tr></thead>
                <tbody>
                  {volunteers.map((v) => (
                    <tr key={v.id} data-testid={`volunteer-row-${v.id}`} className="border-b border-brand-line/50 last:border-0">
                      <td className="px-5 py-3.5 font-medium text-white">{v.name}</td>
                      <td className="px-5 py-3.5 text-brand-cyan">{v.event}</td>
                      <td className="px-5 py-3.5 text-xs text-slate-400">{v.email}<br />{v.phone}</td>
                      <td className="px-5 py-3.5 text-slate-400">{v.city}</td>
                      <td className="px-5 py-3.5 text-xs text-slate-500">{v.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                  {volunteers.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">No volunteer sign-ups yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <div>
            <h1 className="font-display text-3xl font-bold uppercase text-white">Enquiries ({enquiries.length})</h1>
            <div className="mt-6 space-y-4">
              {enquiries.map((e) => (
                <div key={e.id} data-testid={`enquiry-${e.id}`} className="card-k p-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="rounded-full border border-brand-cyan/40 bg-brand-cyan/10 px-3 py-0.5 font-mono text-[10px] tracking-[0.15em] text-brand-cyan">{e.type}</span>
                    <p className="font-semibold text-white">{e.name}{e.organization ? ` — ${e.organization}` : ""}</p>
                    <span className="ml-auto font-mono text-[10px] text-slate-600">{e.created_at?.slice(0, 10)}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-300">{e.message}</p>
                  <p className="mt-2 text-xs text-slate-500">{e.email}{e.phone ? ` • ${e.phone}` : ""}</p>
                </div>
              ))}
              {enquiries.length === 0 && <p className="text-slate-500">No enquiries yet.</p>}
            </div>
          </div>
        )}

        {tab === "stories" && (
          <div>
            <div className="flex items-center justify-between">
              <h1 className="font-display text-3xl font-bold uppercase text-white">Stories ({stories.length})</h1>
              <button data-testid="admin-add-story" onClick={() => setStoryForm({ title: "", slug: "", category: "Destination Stories", date: new Date().toISOString().slice(0, 10), image: "", excerpt: "", content: "" })} className="btn-kinetic btn-primary-k !px-5 !py-2.5 text-xs"><Plus size={15} /> ADD STORY</button>
            </div>
            {storyForm && (
              <form onSubmit={saveStory} className="card-k mt-6 space-y-4 p-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="story-title" required placeholder="Title" className="input-k" value={storyForm.title} onChange={(e) => setStoryForm({ ...storyForm, title: e.target.value })} />
                  <input data-testid="story-category" placeholder="Category" className="input-k" value={storyForm.category} onChange={(e) => setStoryForm({ ...storyForm, category: e.target.value })} />
                  <input data-testid="story-date" type="date" className="input-k" value={storyForm.date} onChange={(e) => setStoryForm({ ...storyForm, date: e.target.value })} />
                  <input data-testid="story-image" placeholder="Image URL" className="input-k" value={storyForm.image} onChange={(e) => setStoryForm({ ...storyForm, image: e.target.value })} />
                </div>
                <textarea data-testid="story-excerpt" placeholder="Excerpt" rows={2} className="input-k" value={storyForm.excerpt} onChange={(e) => setStoryForm({ ...storyForm, excerpt: e.target.value })} />
                <textarea data-testid="story-content" placeholder="Content (blank line between paragraphs)" rows={6} className="input-k" value={storyForm.content} onChange={(e) => setStoryForm({ ...storyForm, content: e.target.value })} />
                <div className="flex gap-3">
                  <button data-testid="story-save" className="btn-kinetic btn-primary-k !px-5 !py-2 text-xs">SAVE STORY</button>
                  <button type="button" onClick={() => setStoryForm(null)} className="btn-kinetic btn-ghost-k !px-5 !py-2 text-xs">CANCEL</button>
                </div>
              </form>
            )}
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {stories.map((s) => (
                <div key={s.id} className="card-k flex items-center justify-between gap-4 p-5">
                  <div><p className="font-medium text-white">{s.title}</p><p className="font-mono text-[10px] text-slate-500">{s.category} • {s.date}</p></div>
                  <div className="flex gap-2">
                    <button data-testid={`story-edit-${s.slug}`} onClick={() => setStoryForm(s)} className="rounded-lg border border-brand-line p-2 text-slate-400 hover:text-brand-cyan"><Pencil size={14} /></button>
                    <button data-testid={`story-delete-${s.slug}`} onClick={async () => { if (window.confirm("Delete story?")) { await api.delete(`/admin/stories/${s.id}`); load(); } }} className="rounded-lg border border-brand-line p-2 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div>
            <h1 className="font-display text-3xl font-bold uppercase text-white">Gallery ({gallery.length})</h1>
            <form onSubmit={addGallery} className="card-k mt-6 grid gap-4 p-6 sm:grid-cols-4">
              <input data-testid="gallery-url" required placeholder="Image URL" className="input-k sm:col-span-2" value={galleryForm.url} onChange={(e) => setGalleryForm({ ...galleryForm, url: e.target.value })} />
              <select data-testid="gallery-category" className="input-k" value={galleryForm.category} onChange={(e) => setGalleryForm({ ...galleryForm, category: e.target.value })}>
                {["EVENTS", "RUNNERS", "DESTINATIONS", "TRAILS", "CULTURE", "COMMUNITIES", "BEHIND THE SCENES"].map((c) => <option key={c}>{c}</option>)}
              </select>
              <input data-testid="gallery-caption" placeholder="Caption" className="input-k" value={galleryForm.caption} onChange={(e) => setGalleryForm({ ...galleryForm, caption: e.target.value })} />
              <button data-testid="gallery-add" className="btn-kinetic btn-primary-k !px-5 !py-2 text-xs sm:col-span-4 sm:w-fit">ADD PHOTO</button>
            </form>
            <div className="mt-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {gallery.map((g) => (
                <div key={g.id} className="group relative overflow-hidden rounded-xl border border-brand-line">
                  <img src={g.url} alt={g.caption} className="h-36 w-full object-cover" />
                  <button data-testid={`gallery-delete-${g.id}`} onClick={async () => { await api.delete(`/admin/gallery/${g.id}`); load(); }} className="absolute right-2 top-2 rounded-lg bg-brand-ink/80 p-1.5 text-slate-300 opacity-0 transition-opacity hover:text-red-400 group-hover:opacity-100"><Trash2 size={13} /></button>
                  <p className="absolute bottom-0 w-full bg-brand-ink/80 px-2 py-1 font-mono text-[9px] tracking-wider text-brand-olive">{g.category}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "partners" && (
          <div>
            <h1 className="font-display text-3xl font-bold uppercase text-white">Partners ({partners.length})</h1>
            <p className="mt-1 text-sm text-slate-400">These logos scroll on the homepage and Partners page. Add new partner logos here anytime.</p>
            <form onSubmit={addPartner} className="card-k mt-6 grid gap-4 p-6 sm:grid-cols-3">
              <input data-testid="partner-name-input" required placeholder="Partner name" className="input-k" value={partnerForm.name} onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })} />
              <input data-testid="partner-logo-input" placeholder="Logo image URL (optional)" className="input-k" value={partnerForm.logo} onChange={(e) => setPartnerForm({ ...partnerForm, logo: e.target.value })} />
              <input data-testid="partner-url-input" placeholder="Website URL" className="input-k" value={partnerForm.url} onChange={(e) => setPartnerForm({ ...partnerForm, url: e.target.value })} />
              <button data-testid="partner-add" className="btn-kinetic btn-primary-k !px-5 !py-2 text-xs sm:col-span-3 sm:w-fit">ADD PARTNER</button>
            </form>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {partners.map((p) => (
                <div key={p.id} data-testid={`partner-row-${p.id}`} className="card-k flex items-center gap-4 p-4">
                  <span className="flex h-12 w-24 shrink-0 items-center justify-center rounded-lg bg-white px-2">
                    {p.logo ? <img src={p.logo} alt={p.name} className="max-h-8 w-auto object-contain" /> : <span className="text-center font-display text-[10px] font-bold uppercase text-brand-ink">{p.name}</span>}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white">{p.name}</p>
                    <p className="truncate font-mono text-[10px] text-slate-500">{p.url}</p>
                  </div>
                  <button data-testid={`partner-edit-${p.id}`} onClick={() => {
                    const name = window.prompt("Partner name", p.name); if (name === null) return;
                    const logo = window.prompt("Logo image URL (blank for text badge)", p.logo || ""); if (logo === null) return;
                    const url = window.prompt("Website URL", p.url || ""); if (url === null) return;
                    api.put(`/admin/partners/${p.id}`, { ...p, name, logo, url }).then(() => { toast.success("Partner updated"); load(); }).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
                  }} className="rounded-lg border border-brand-line p-2 text-slate-400 hover:text-brand-cyan"><Pencil size={14} /></button>
                  <button data-testid={`partner-delete-${p.id}`} onClick={async () => { if (window.confirm("Remove this partner?")) { await api.delete(`/admin/partners/${p.id}`); load(); } }} className="rounded-lg border border-brand-line p-2 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "team" && (
          <div>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-display text-3xl font-bold uppercase text-white">Team ({team.length})</h1>
                <p className="mt-1 text-sm text-slate-400">Shown on the About page. Add names, short bios and photos.</p>
              </div>
              <button data-testid="admin-add-member" onClick={() => setMemberForm({ name: "", role: "", bio: "", photo: "" })} className="btn-kinetic btn-primary-k !px-5 !py-2.5 text-xs"><Plus size={15} /> ADD MEMBER</button>
            </div>
            {memberForm && (
              <form onSubmit={saveMember} className="card-k mt-6 space-y-4 p-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input data-testid="member-name" placeholder="Name" className="input-k" value={memberForm.name} onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} />
                  <input data-testid="member-role" required placeholder="Role (e.g. Founder & Race Director)" className="input-k" value={memberForm.role} onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })} />
                </div>
                <input data-testid="member-photo" placeholder="Photo URL" className="input-k" value={memberForm.photo} onChange={(e) => setMemberForm({ ...memberForm, photo: e.target.value })} />
                <textarea data-testid="member-bio" placeholder="Short bio" rows={3} className="input-k" value={memberForm.bio} onChange={(e) => setMemberForm({ ...memberForm, bio: e.target.value })} />
                <div className="flex gap-3">
                  <button data-testid="member-save" className="btn-kinetic btn-primary-k !px-5 !py-2 text-xs">SAVE MEMBER</button>
                  <button type="button" onClick={() => setMemberForm(null)} className="btn-kinetic btn-ghost-k !px-5 !py-2 text-xs">CANCEL</button>
                </div>
              </form>
            )}
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {team.map((m) => (
                <div key={m.id} data-testid={`team-row-${m.id}`} className="card-k overflow-hidden">
                  {m.photo && <img src={m.photo} alt={m.name || m.role} className="h-36 w-full object-cover object-top" />}
                  <div className="flex items-start justify-between gap-3 p-4">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-white">{m.name || m.role}</p>
                      {m.name && <p className="font-mono text-[10px] tracking-wider text-brand-olive">{m.role}</p>}
                      <p className="mt-1 line-clamp-2 text-xs text-slate-400">{m.bio}</p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button data-testid={`team-edit-${m.id}`} onClick={() => setMemberForm(m)} className="rounded-lg border border-brand-line p-2 text-slate-400 hover:text-brand-cyan"><Pencil size={14} /></button>
                      <button data-testid={`team-delete-${m.id}`} onClick={async () => { if (window.confirm("Remove team member?")) { await api.delete(`/admin/team/${m.id}`); load(); } }} className="rounded-lg border border-brand-line p-2 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "settings" && settings && (
          <div className="max-w-xl">
            <h1 className="font-display text-3xl font-bold uppercase text-white">Site Settings</h1>
            <form onSubmit={saveSettings} className="card-k mt-6 space-y-4 p-6">
              {[["CONTACT EMAIL (GENERAL)", "contact_email"], ["EVENT QUERIES EMAIL", "event_email"], ["PHONE (DISPLAY)", "phone"], ["WHATSAPP (country code + number)", "whatsapp"], ["FACEBOOK URL", "facebook"], ["INSTAGRAM URL", "instagram"], ["TWITTER / X URL", "twitter"], ["YOUTUBE URL", "youtube"], ["ADDRESS", "address"], ["ADDITIONAL PHONES (comma separated)", "phones"], ["OFFICIAL INFO EMAIL", "info_email"], ["DONATION LINK (Razorpay payment page URL)", "donation_link"], ["DONATION UPI ID", "donation_upi"], ["DONATION BENEFICIARY NAME", "donation_beneficiary"]].map(([label, k]) => (
                <label key={k} className="block">
                  <span className="mb-1.5 block font-mono text-[10px] tracking-[0.2em] text-slate-500">{label}</span>
                  <input data-testid={`settings-${k}`} className="input-k" value={Array.isArray(settings[k]) ? settings[k].join(", ") : settings[k] || ""} onChange={(e) => setSettings({ ...settings, [k]: e.target.value })} />
                </label>
              ))}
              <button data-testid="settings-save" className="btn-kinetic btn-primary-k !px-6 !py-2.5 text-xs">SAVE SETTINGS</button>
            </form>
          </div>
        )}
      </main>

      {editing !== undefined && (
        <EventForm initial={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
}
